import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MapPinIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMeeting } from "../../contexts/MeetingContext";
import { useAuth } from "../../contexts/AuthContext";

const StartMeetingButton = ({
  participantId,
  participantName,
  onStart,
  onError,
  disabled = false,
  className = "",
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { meetingStatus, meeting, requestMeeting, leaveMeeting } = useMeeting();
  const [loading, setLoading] = useState(false);

  const gpsAvailable =
    typeof navigator !== "undefined" && "geolocation" in navigator;

  useEffect(() => {
    if (meetingStatus === "active" && meeting?.meetingId) {
      navigate(`/meeting/${meeting.meetingId}`);
      onStart?.();
    }
  }, [meetingStatus, meeting?.meetingId, navigate, onStart]);

  const handleClick = async () => {
    if (!gpsAvailable) {
      const msg = "GPS is not available on this device";
      toast.error(msg);
      onError?.(msg);
      return;
    }

    setLoading(true);
    try {
      const creatorInfo = {
        id: user?._id || user?.id,
        firstName: user?.firstName,
        lastName: user?.lastName,
      };
      await requestMeeting(participantId, creatorInfo);
      toast.success(`Meeting request sent to ${participantName || "participant"}`);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to start meeting";
      toast.error(msg);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    leaveMeeting();
    toast.success("Meeting request cancelled");
  };

  const isRequesting = meetingStatus === "requesting";

  const isDisabled =
    disabled || loading;

  if (isRequesting) {
    return (
      <div className="flex items-center space-x-2">
        <span className="text-sm text-blue-600 font-medium animate-pulse">
          Waiting for acceptance...
        </span>
        <button
          onClick={handleCancel}
          className="flex items-center space-x-1 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium py-1.5 px-3 rounded-md transition-colors"
        >
          <XMarkIcon className="h-4 w-4" />
          <span>Cancel</span>
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={`flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors ${className}`}
    >
      <MapPinIcon className="h-5 w-5" />
      <span>
        {loading ? "Sending..." : "Share Live Location"}
      </span>
    </button>
  );
};

export default StartMeetingButton;
